/**
 * مسیر پایه و مسیر عمومی نسخه‌ی مشتری.
 *
 * یک build، دو مخاطب. تفاوت را **سرور** تعیین می‌کند (از میزبان درخواست) و
 * فرانت‌اند آن را از `/api/meta` می‌گیرد. تنها چیزی که این ماژول از URL
 * می‌خواند، «مسیر عمومی» است:
 *
 * ```text
 * https://assistant.example.com/acme        → slug = acme
 * https://assistant.example.com/acme/archive → slug = acme، مسیر داخلی /archive
 * https://di.example.com/studio/dossiers     → پنل، بدون slug
 * ```
 *
 * چرا slug از URL می‌آید و مخاطب از سرور: slug باید **پیش از** نخستین درخواست
 * معلوم باشد (خودِ `meta` با آن پرسیده می‌شود)، اما مخاطب چیزی است که کلاینت
 * نباید تعیینش کند.
 */

declare global {
  interface Window {
    /** مسیر پایه، اگر سرویس پشت proxy زیر یک مسیر نشسته باشد. */
    __DI_BASE_PATH__?: string
  }
}

/** مسیرهایی که مسیر عمومی نیستند و به خودِ اپ تعلق دارند. */
const RESERVED = new Set(['api', 'assets', 'studio', 'consult', 'archive', 'login'])

/** مسیر پایه‌ی تزریق‌شده توسط سرور، بدون `/` انتهایی. */
const injectedBase = (): string => (window.__DI_BASE_PATH__ ?? '').replace(/\/+$/, '')

/**
 * نخستین بخش مسیر، اگر مسیر عمومی به‌نظر برسد.
 *
 * تنها روی مخاطب مشتری معنا دارد؛ فراخواننده باید با `meta.audience` تصمیم
 * بگیرد. اینجا فقط شکل مسیر بررسی می‌شود.
 */
export const slugFromPath = (): string => {
  const base = injectedBase()
  const path = window.location.pathname.slice(base.length)
  const first = path.split('/').filter((part) => part !== '')[0] ?? ''
  if (first === '' || RESERVED.has(first)) {
    return ''
  }
  // شکل مجاز همان است که سرور می‌پذیرد: حرف کوچک لاتین، رقم و خط تیره.
  return /^[a-z0-9-]+$/.test(first) ? first : ''
}

/**
 * مسیر پایه‌ی مسیریابی مرورگری.
 *
 * برای نسخه‌ی مشتری شامل مسیر عمومی است تا `/archive` داخل همان نسخه بماند.
 */
export const routerBase = (): string => {
  const slug = slugFromPath()
  return slug === '' ? injectedBase() : `${injectedBase()}/${slug}`
}

/** پایه‌ی آدرس API. مسیر عمومی جزو آن **نیست**؛ slug به‌شکل پارامتر می‌رود. */
export const apiBase = (): string => injectedBase()

/** آدرس مطلق یک مسیر داخلی. برای کپی کردن و لینک بیرونی. */
export const absoluteUrl = (path: string): string =>
  `${window.location.origin}${routerBase()}${path}`
